import { IconType } from 'react-icons'

export type ApprovalTabKey = 'payables' | 'actions' | 'refunds' | 'approved'

export interface ApprovalTabDef {
    key: ApprovalTabKey
    label: string
    icon: IconType
    count: number
    /** Fila que movimenta dinheiro para fora — o badge fica em destaque. */
    highlight?: boolean
}

interface ApprovalTabsProps {
    tabs: ApprovalTabDef[]
    active: ApprovalTabKey
    onChange: (key: ApprovalTabKey) => void
    loading?: boolean
}

/**
 * Navegação entre as filas.
 *
 * Cada fila tem rito próprio (senha, operador eleito, categoria de motivo) e antes
 * disso vinham empilhadas na mesma tela — o operador rolava a página inteira para
 * descobrir que não havia nada pendente embaixo. Com abas, a contagem fica visível
 * de imediato e cada fila pagina sozinha.
 *
 * Rola horizontalmente em telas pequenas em vez de quebrar em duas linhas: quatro
 * abas empilhadas empurrariam a lista para fora da primeira dobra no celular.
 */
export default function ApprovalTabs({
    tabs,
    active,
    onChange,
    loading = false,
}: ApprovalTabsProps) {
    return (
        <div
            role="tablist"
            // `overflow-y-hidden` é obrigatório junto do `overflow-x-auto`: com um eixo
            // em `auto` e o outro em `visible`, o navegador promove o `visible` para
            // `auto` — e o `-mb-px` dos botões (o 1px que faz a aba ativa cobrir a
            // borda do container) passava a gerar uma barra de rolagem vertical.
            className="flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-base-300/70 -mx-1 px-1"
        >
            {tabs.map(tab => {
                const isActive = tab.key === active
                const Icon = tab.icon
                return (
                    <button
                        key={tab.key}
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => onChange(tab.key)}
                        className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                            isActive
                                ? 'border-secondary text-secondary'
                                : 'border-transparent text-base-content/60 hover:text-base-content hover:border-base-300'
                        }`}
                    >
                        <Icon className="text-lg shrink-0" />
                        {tab.label}
                        <span
                            className={`badge badge-sm border-0 ${
                                isActive
                                    ? 'bg-secondary text-secondary-content'
                                    : tab.count > 0 && tab.highlight
                                      ? 'bg-secondary/15 text-secondary'
                                      : 'bg-base-200 text-base-content/60'
                            }`}
                        >
                            {loading ? '—' : tab.count}
                        </span>
                    </button>
                )
            })}
        </div>
    )
}
